/**
 * The Race ledger (prototype B): a navigator grouped by Body, a filter dock
 * (Endorsed, Incumbents, questionnaire status) and a bounded ledger. Selecting
 * a Race scrolls to it and records `race` with replaceState; filters hide
 * Races with no matching candidate, as the prototype does.
 */
import type {SelectedCycleRace} from "../ballot-model";
import type { PublicOfficeholder, PublicRace } from "../api";
import { h, uid } from "../dom";
import { RESPONSE_LABEL } from "../labels";
import { personHref } from "../links";
import { jurisdictionLabel } from "../model";
import { raceScrollOffset } from "./race-scroll-spy";
import { buildUrl, RESPONSE_FILTERS, type ElectionFilters, type ResponseFilter } from "../router";
import { candidateRow } from "./candidate-row";
import { multiSelect } from "./dropdown";
import { attachRaceScrollSpy, scrollRaceIntoView } from "./race-scroll-spy";
import { attachDockState } from "./ballot-finder";

export interface LedgerOptions {
  signal: AbortSignal;
  page: URL;
  cycle: string;
  ballot: { slug: string; stage: string };
  /** The ballot param as the URL carries it (null when on the live ballot by default). */
  ballotParam: string | null;
  selectedRace: string | null;
  filters: ElectionFilters;
  /** My Ballot replaces editable filters with its read-only address context. */
  showFilters?: boolean;
  /** Ballot control stays available in My Ballot while ordinary filters hide. */
  ballotControl?: HTMLElement | null;
  /** Record within-view state without a history entry. */
  replace(href: string): void;
  /** Present once comparison exists; shown only on Races with a completed Response. */
  onCompare?: (race: PublicRace, trigger: HTMLElement) => void;
}

/** The level of government a Body belongs to ("City", "County"…), or null when Backbone has none. */
export function bodyLevel(race: PublicRace): string | null {
  const jurisdiction = race.body.jurisdiction?.trim();
  return jurisdiction ? jurisdictionLabel(jurisdiction) : null;
}

/**
 * A Body's heading in the ledger. It docks under the finder while its Races scroll
 * past, and names the level of government above the Body so a long ballot keeps
 * City, County, State and Federal apart.
 */
export function raceGroupHead(body: string, level: string | null): HTMLElement {
  return h(
    "header",
    { class: "bsc-race-group__head" },
    level ? h("p", { class: "bsc-race-group__level" }, level) : null,
    h("h2", { class: "bsc-race-group__title" }, body)
  );
}

/** Races grouped under their Body, in the order Backbone sorted them. */
export function groupByBody(races: PublicRace[]): Map<string, PublicRace[]> {
  const groups = new Map<string, PublicRace[]>();
  for (const race of races) {
    const key = race.body.name ?? race.body.slug;
    groups.set(key, [...(groups.get(key) ?? []), race]);
  }
  return groups;
}

export function raceMatches(race: PublicRace, f: ElectionFilters): boolean {
  if ("eligible" in race && !race.eligible) return false;
  if (f.endorsed && !race.candidates.some((c) => c.endorsement.endorsed || ("all" in race && race.all && "historicalEndorsed" in c && c.historicalEndorsed))) return false;
  if (f.incumbents && !race.candidates.some((c) => c.isIncumbent)) return false;
  if (f.responses && !race.candidates.some((c) => c.responseStatus !== null && f.responses!.includes(c.responseStatus))) return false;
  return true;
}

function contestId(race: PublicRace): string {
  return `bsc-race-${race.slug}`;
}

export function officeholderRow(holder: PublicOfficeholder, page: URL, cycle: string, race: string): HTMLElement {
  const href = personHref(page, holder.publicUrl, { from: cycle, race });
  return h(
    "li",
    { class: "bsc-officeholder" },
    h("span", { class: "bsc-officeholder__label" }, "Current officeholder"),
    href ? h("a", { class: "bsc-officeholder__name", href }, holder.name) : h("span", { class: "bsc-officeholder__name" }, holder.name)
  );
}

function raceMeta(race: PublicRace): HTMLElement {
  const completed = race.candidates.filter((c) => c.responseStatus === "completed").length;
  const n = race.candidates.length;
  return h(
    "p",
    { class: "bsc-race__meta" },
    h("span", null, `Elects ${race.seatsToFill} ${race.seatsToFill === 1 ? "seat" : "seats"}`),
    n ? h("span", null, `${n} ${n === 1 ? "candidate" : "candidates"}`) : null,
    // Before the first publish date nothing about responses is public, not even a count.
    n && !race.questionnaireNotice ? h("span", null, `${completed} completed ${completed === 1 ? "questionnaire" : "questionnaires"}`) : null
  );
}

export function renderRaceSection(race: PublicRace, opts: {
  page: URL;
  cycle: string;
  stage: string;
  onCompare?: LedgerOptions["onCompare"];
}): HTMLElement {
  const hasCompleted = race.candidates.some((c) => c.responseStatus === "completed");
  const compare =
    opts.onCompare && hasCompleted && !race.questionnaireNotice
      ? h("button", { type: "button", class: "bsc-race__compare", "data-compare": race.slug }, "Compare questionnaires")
      : null;
  compare?.addEventListener("click", () => opts.onCompare!(race, compare));
  const rowCtx = { page: opts.page, cycle: opts.cycle, race: race.slug, stage: opts.stage };
  const body = race.candidates.length
    ? h("ul", { class: "bsc-race__candidates" }, race.candidates.map((c: PublicRace["candidates"][number]) => candidateRow(c, rowCtx)))
    : h(
        "div",
        { class: "bsc-race__none" },
        h("p", { class: "bsc-race__none-copy" }, "No public candidates yet."),
        race.currentHolders.length
          ? h("ul", { class: "bsc-race__holders" }, race.currentHolders.map((o) => officeholderRow(o, opts.page, opts.cycle, race.slug)))
          : h("p", { class: "bsc-race__none-copy" }, "No current officeholder is published.")
      );
  return h(
    "section",
    { class: `bsc-race${"all" in race && race.all ? " bsc-race--all" : ""}`, id: contestId(race), "data-race-slug": race.slug, "aria-labelledby": `${contestId(race)}-title` },
    h(
      "header",
      { class: "bsc-race__header" },
      h(
        "div",
        null,
        h("h3", { class: "bsc-race__name", id: `${contestId(race)}-title` }, race.name),
        raceMeta(race),
        race.questionnaireNotice ? h("p", { class: "bsc-race__questionnaire-notice" }, race.questionnaireNotice.message) : null
      ),
      compare
    ),
    body,
    "folds" in race ? (race as SelectedCycleRace).folds.map(group=>h("details",{class:"bsc-race__earlier"},
      h("summary",{class:"bsc-race__earlier-summary"},
        h("span",{class:"bsc-race__earlier-label"},group.label),
        h("span",{class:"bsc-race__earlier-count"},String(group.candidates.length)),
        h("span",{class:"bsc-sr-only"},group.candidates.length === 1 ? " candidate" : " candidates"),
        group.endorsedCount ? h("span",{class:"bsc-race__earlier-endorsed"},`${group.endorsedCount} endorsed`) : null,
        h("span",{class:"bsc-race__earlier-action","aria-hidden":"true"})),
      h("ul",{class:"bsc-race__candidates"},group.candidates.map(c=>candidateRow(c,rowCtx))))) : null
  );
}

export function renderRaceLedger(races: PublicRace[], opts: LedgerOptions): { nav: HTMLElement; main: HTMLElement } {
  const filters: ElectionFilters = { ...opts.filters, responses: opts.filters.responses ? [...opts.filters.responses] : null };
  let selected = races.some((r) => r.slug === opts.selectedRace) ? opts.selectedRace : null;
  let current = races.find((r) => r.slug === selected && raceMatches(r, filters))?.slug
    ?? races.find((r) => raceMatches(r, filters))?.slug ?? null;
  const groups = groupByBody(races);
  const groupControls = new Map<string, { button: HTMLElement; list: HTMLElement; count: HTMLElement }>();
  const bodyOf = (race: PublicRace) => race.body.name ?? race.body.slug;

  // ---- Navigator ---------------------------------------------------------
  // One Body is open at a time, as in prototype B and the Lookup tool. Every Body
  // heading pins inside the navigator's one scroll panel: headings up to the open
  // Body stack at the top, the rest wait at the bottom, so the open Body's Races
  // scroll between them (syncPins).
  const navBodyId = uid("race-nav");
  const raceUrl = (slug: string | null) =>
    buildUrl({ kind: "election", cycle: opts.cycle, ballot: opts.ballotParam, race: slug }, opts.page, filters);
  const nav = h("nav", { class: "bsc-race-nav", "aria-label": "Races on this ballot" });
  // Docked on a phone, the bar names the Race being read (prototype B); open or at
  // rest it offers the whole list. CSS shows one label at a time.
  const navExplore = h("span", { class: "bsc-race-nav__explore" }, `Explore ${races.length} ${races.length === 1 ? "Race" : "Races"}`);
  const navCurrent = h("span", { class: "bsc-race-nav__current" });
  const navToggle = h(
    "button",
    { type: "button", class: "bsc-race-nav__toggle", "aria-expanded": "false", "aria-controls": navBodyId },
    navExplore,
    navCurrent
  );
  const initialBody = (() => {
    const race = races.find((r) => r.slug === current);
    return race ? bodyOf(race) : [...groups.keys()][0];
  })();
  const navBody = h(
    "div",
    { class: "bsc-race-nav__body", id: navBodyId },
    [...groups].map(([body, list], index, all) => {
      const listId = uid("race-nav-group");
      const open = body === initialBody;
      const count = h("span", { class: "bsc-race-nav__group-count" });
      // The first Body of each level of government carries the level above its name.
      const level = bodyLevel(list[0]);
      const startsLevel = level !== null && (index === 0 || bodyLevel(all[index - 1][1][0]) !== level);
      const button = h("button", {
        type: "button", class: "bsc-race-nav__group-toggle", "aria-expanded": String(open), "aria-controls": listId,
        "data-level-start": startsLevel ? true : null,
      }, h("span", { class: "bsc-race-nav__group-label" },
        startsLevel ? h("span", { class: "bsc-race-nav__group-level" }, level) : null,
        h("span", { class: "bsc-race-nav__group-title" }, body)), count);
      const raceList = h(
          "ul", { id: listId, class: "bsc-race-nav__list", hidden: !open },
          list.map((r) => h("li", null, h("a", {
            class: "bsc-race-nav__link", href: raceUrl(r.slug), "data-bsc-local": true,
            "data-race-slug": r.slug, "aria-current": r.slug === current ? "true" : null,
          }, h("span", { class: "bsc-race-nav__name" }, r.name),
          h("span", { class: "bsc-race-nav__count" }, r.candidates.length
            ? `${r.candidates.length} ${r.candidates.length === 1 ? "candidate" : "candidates"}` : "No candidates yet"))))
      );
      groupControls.set(body, { button, list: raceList, count });
      // A Body heading opens that Body and takes the page to it.
      button.addEventListener("click", () => {
        const visible = list.filter((r) => raceMatches(r, filters));
        const race = visible.find((r) => r.slug === current) ?? visible[0];
        if (race) goToRace(race.slug, { closeMobile: false });
        else activateGroup(body);
      });
      return h(
        "div",
        { class: "bsc-race-nav__group", "data-group": body, "data-level": level },
        button, raceList
      );
    })
  );
  // Back to the top of the Tool: the phone navigator's companion button (prototype B).
  const navTop = h("button", { type: "button", class: "bsc-race-nav__top", "aria-label": "Back to the top of the Races" });
  navTop.addEventListener("click", () => {
    const tool = nav.closest<HTMLElement>(".bsc-election__tool") ?? nav;
    const top = Math.max(0, window.scrollY + tool.getBoundingClientRect().top - 12);
    window.scrollTo({ top, behavior: window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth" });
  });
  nav.append(h("div", { class: "bsc-race-nav__bar" }, navToggle, navTop), navBody);
  navToggle.addEventListener("click", () => {
    const open = navToggle.getAttribute("aria-expanded") !== "true";
    navToggle.setAttribute("aria-expanded", String(open));
    nav.classList.toggle("is-open", open);
    if (open) dockOpenNav();
    syncPins();
    revealCurrent();
  });

  /** Opened on a phone, the navigator takes the whole screen: the stylesheet moves the
   *  site header aside, and the page scrolls so the bar sits at its docked position. */
  function dockOpenNav() {
    if (!window.getComputedStyle || typeof nav.getBoundingClientRect !== "function") return;
    if (window.getComputedStyle(nav).position !== "sticky") return;
    // The docked bar sits at --bsc-top. Read that, not the bar's own `top`, which is
    // still easing from the header's offset (a transition) when the bar opens.
    const dockTop = parseFloat(window.getComputedStyle(nav).getPropertyValue("--bsc-top")) || 0;
    const distance = nav.getBoundingClientRect().top - dockTop;
    if (distance <= 1) return;
    // A jump, not a glide: iOS Safari cuts a smooth scroll short as its toolbar resizes.
    window.scrollTo({ top: window.scrollY + distance, behavior: "instant" as ScrollBehavior });
  }

  function activateGroup(body: string) {
    for (const [name, group] of groupControls) {
      const open = name === body;
      group.button.setAttribute("aria-expanded", String(open));
      group.list.hidden = !open;
    }
    syncPins();
  }

  /** Lookup tool rule (lib/sticky-offsets.ts there; syncStickyNavHeaders in prototype B):
   *  headings through the last open one pin to the top, each closed one holding its own
   *  slot; every later heading pins to the bottom in order. --bsc-nav-pin-top/bottom give
   *  the space they cover, so the current Race can be revealed between them. */
  function syncPins() {
    const toggles = Array.from(navBody.querySelectorAll<HTMLElement>(".bsc-race-nav__group:not([hidden]) > .bsc-race-nav__group-toggle"));
    if (!toggles.length) return;
    // Slots use each heading's own height, so a Body name that wraps keeps its full text.
    const heights = toggles.map((toggle) =>
      Math.ceil(typeof toggle.getBoundingClientRect === "function" ? toggle.getBoundingClientRect().height : 0) || 48);
    let lastOpen = -1;
    toggles.forEach((toggle, index) => { if (toggle.getAttribute("aria-expanded") === "true") lastOpen = index; });
    let pinnedAbove = 0;
    toggles.forEach((toggle, index) => {
      if (index <= lastOpen) {
        toggle.style.top = `${pinnedAbove}px`;
        toggle.style.bottom = "auto";
        if (toggle.getAttribute("aria-expanded") !== "true") pinnedAbove += heights[index];
      } else {
        toggle.style.top = "auto";
        toggle.style.bottom = `${heights.slice(index + 1).reduce((sum, value) => sum + value, 0)}px`;
      }
    });
    const openHeight = lastOpen >= 0 ? heights[lastOpen] : 0;
    navBody.style.setProperty("--bsc-nav-pin-top", `${pinnedAbove + openHeight}px`);
    navBody.style.setProperty("--bsc-nav-pin-bottom", `${heights.slice(lastOpen + 1).reduce((sum, value) => sum + value, 0)}px`);
  }

  /** Keeps the current Race in view between the pinned headings, as prototype B does. */
  function revealCurrent() {
    if (!current || !window.getComputedStyle || typeof navBody.scrollTo !== "function") return;
    if (!(navBody.clientHeight > 0) || navBody.scrollHeight <= navBody.clientHeight) return;
    const link = navBody.querySelector<HTMLElement>(`.bsc-race-nav__link[data-race-slug="${CSS.escape(current)}"]`);
    if (!link || typeof link.getBoundingClientRect !== "function") return;
    const item = link.getBoundingClientRect();
    const box = navBody.getBoundingClientRect();
    const style = window.getComputedStyle(navBody);
    const pinTop = parseFloat(style.getPropertyValue("--bsc-nav-pin-top")) || 0;
    const pinBottom = parseFloat(style.getPropertyValue("--bsc-nav-pin-bottom")) || 0;
    const visibleTop = box.top + pinTop;
    const visibleBottom = box.bottom - pinBottom;
    if (item.top >= visibleTop && item.bottom <= visibleBottom) return;
    const desiredTop = visibleTop + Math.max(0, (visibleBottom - visibleTop - item.height) / 2);
    navBody.scrollTo({ top: navBody.scrollTop + item.top - desiredTop, behavior: "smooth" });
  }

  function markSelected(slug: string | null) {
    current = slug;
    nav.querySelectorAll<HTMLElement>(".bsc-race-nav__link").forEach((a) => {
      if (a.dataset.raceSlug === slug) a.setAttribute("aria-current", "true");
      else a.removeAttribute("aria-current");
    });
    const race = races.find((r) => r.slug === slug);
    // The docked phone bar names the Body above the Race, so the reader keeps both.
    navCurrent.replaceChildren(...(race ? [
      h("span", { class: "bsc-race-nav__current-body" }, [bodyLevel(race), bodyOf(race)].filter(Boolean).join(" · ")),
      h("span", { class: "bsc-race-nav__current-name" }, race.name),
    ] : []));
    if (race) activateGroup(bodyOf(race));
    revealCurrent();
  }

  function goToRace(slug: string, { closeMobile }: { closeMobile: boolean }) {
    selected = slug;
    markSelected(selected);
    opts.replace(raceUrl(selected));
    const target = ledger.querySelector<HTMLElement>(`.bsc-race[data-race-slug="${CSS.escape(selected)}"]:not([hidden])`);
    if (closeMobile) {
      navToggle.setAttribute("aria-expanded", "false");
      nav.classList.remove("is-open");
    }
    if (target) {
      scrollSpy?.suspendForScroll(target, scrollRaceIntoView(target, { animate: true }));
      // Move focus with the reader, as an in-page link would.
      target.setAttribute("tabindex", "-1");
      target.focus({ preventScroll: true });
    }
  }

  nav.addEventListener("click", (event) => {
    const link = (event.target as Element).closest<HTMLAnchorElement>(".bsc-race-nav__link");
    if (!link || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
    event.preventDefault();
    if (link.dataset.raceSlug) goToRace(link.dataset.raceSlug, { closeMobile: true });
  });
  window.addEventListener("resize", () => syncPins(), { passive: true, signal: opts.signal });
  // Heading heights are only known once the navigator is on the page and its fonts load.
  if (typeof ResizeObserver !== "undefined") {
    const observer = new ResizeObserver(() => syncPins());
    observer.observe(navBody);
    opts.signal.addEventListener("abort", () => observer.disconnect(), { once: true });
  }
  if (typeof requestAnimationFrame !== "undefined") requestAnimationFrame(() => { syncPins(); revealCurrent(); });

  // ---- Filter dock -------------------------------------------------------
  const writeFilters = () => opts.replace(raceUrl(selected));
  const toggleButton = (label: string, key: "endorsed" | "incumbents") => {
    const button = h(
      "button",
      { type: "button", class: `bsc-race-filters__toggle bsc-race-filters__toggle--${key}`, "aria-pressed": String(filters[key]) },
      label
    );
    button.addEventListener("click", () => {
      filters[key] = !filters[key];
      button.setAttribute("aria-pressed", String(filters[key]));
      writeFilters();
      apply();
    });
    return button;
  };
  const allStatuses = [...RESPONSE_FILTERS];
  const dock = h(
    "div",
    { class: "bsc-race-filters", role: "group", "aria-label": "Filter Races" },
    opts.showFilters === false ? null : toggleButton("Endorsed", "endorsed"),
    opts.showFilters === false ? null : toggleButton("Incumbents", "incumbents"),
    opts.ballotControl,
    opts.showFilters === false ? null : multiSelect({
      name: "responses",
      label: "Questionnaire status",
      options: allStatuses.map((s) => ({ value: s, label: RESPONSE_LABEL[s] })),
      values: filters.responses ?? allStatuses,
      onChange: (values) => {
        filters.responses = values.length === allStatuses.length ? null : (values as ResponseFilter[]);
        writeFilters();
        apply();
      },
    })
  );

  // ---- Ledger --------------------------------------------------------------
  const empty = h("p", { class: "bsc-race-ledger__empty", hidden: true }, "No Races match these filters.");
  const ledger = h(
    "div",
    { class: "bsc-race-ledger" },
    [...groups].map(([body, list]) =>
      h(
        "section",
        { class: "bsc-race-group", "data-group": body, "data-level": bodyLevel(list[0]), "aria-label": body },
        raceGroupHead(body, bodyLevel(list[0])),
        list.map((r) => renderRaceSection(r, { ...opts, stage: opts.ballot.stage }))
      )
    ),
    empty
  );
  const status = h("p", { class: "bsc-sr-only", "aria-live": "polite" });

  function apply() {
    let visible = 0;
    const visibleSlugs = new Set<string>();
    for (const race of races) {
      const match = raceMatches(race, filters);
      const el = ledger.querySelector<HTMLElement>(`#${CSS.escape(contestId(race))}`);
      if (el) el.hidden = !match;
      if (match) {
        visible += 1;
        visibleSlugs.add(race.slug);
      }
    }
    ledger.querySelectorAll<HTMLElement>(".bsc-race-group").forEach((g) => {
      g.hidden = !g.querySelector(".bsc-race:not([hidden])");
    });
    nav.querySelectorAll<HTMLElement>(".bsc-race-nav__link").forEach((a) => {
      a.closest("li")!.hidden = !visibleSlugs.has(a.dataset.raceSlug ?? "");
    });
    nav.querySelectorAll<HTMLElement>(".bsc-race-nav__group").forEach((g) => {
      g.hidden = !g.querySelector("li:not([hidden])");
    });
    for (const [body, list] of groups) {
      const count = list.filter((r) => visibleSlugs.has(r.slug)).length;
      const controls = groupControls.get(body)!;
      controls.count.replaceChildren(document.createTextNode(String(count)), h("span", {class:"bsc-sr-only"}, ` ${count === 1 ? "race" : "races"}`));
    }
    navExplore.textContent = `Explore ${visible} ${visible === 1 ? "Race" : "Races"}`;
    if (!current || !visibleSlugs.has(current)) markSelected(races.find((r) => visibleSlugs.has(r.slug))?.slug ?? null);
    empty.hidden = visible > 0;
    status.textContent = `${visible} of ${races.length} Races shown`;
    syncPins();
    scrollSpy?.refresh();
  }
  // Printing opens every disclosure and restores its exact prior state.
  let printState: Map<HTMLDetailsElement, boolean> | null = null;
  const restorePrint = () => {
    printState?.forEach((open,el)=>el.toggleAttribute("open",open));
    printState = null;
  };
  const beforePrint = () => {
    if (printState) return;
    printState = new Map(Array.from(ledger.querySelectorAll<HTMLDetailsElement>(".bsc-race__earlier")).map(el=>[el,el.hasAttribute("open")]));
    printState.forEach((_open,el)=>el.setAttribute("open",""));
  };
  window.addEventListener("beforeprint",beforePrint);
  window.addEventListener("afterprint",restorePrint);
  const disposePrint = () => {
    restorePrint();
    window.removeEventListener("beforeprint",beforePrint);
    window.removeEventListener("afterprint",restorePrint);
  };
  if (opts.signal.aborted) disposePrint();
  else opts.signal.addEventListener("abort",disposePrint,{once:true});
  // The address bar follows the reader: once the ledger is being read, the Race on
  // screen is the `race` a copied or shared link opens to. Above the ledger, there is
  // no Race to name. Debounced, as Safari throttles replaceState.
  let urlTimer: ReturnType<typeof setTimeout> | null = null;
  opts.signal.addEventListener("abort", () => { if (urlTimer) clearTimeout(urlTimer); }, { once: true });
  function followReader(slug: string | null) {
    if (urlTimer) clearTimeout(urlTimer);
    urlTimer = setTimeout(() => {
      urlTimer = null;
      if (opts.signal.aborted || !ledger.isConnected) return;
      const reading = ledger.getBoundingClientRect().top < raceScrollOffset(ledger);
      const next = reading ? slug : null;
      if (next === selected) return;
      selected = next;
      opts.replace(raceUrl(selected));
    }, 250);
  }
  const scrollSpy = attachRaceScrollSpy({
    ledger,
    signal: opts.signal,
    onCurrent: (slug) => { markSelected(slug); followReader(slug); },
  });
  apply();

  if (selected) {
    ledger.querySelector(`.bsc-race[data-race-slug="${CSS.escape(selected)}"]:not([hidden])`)?.setAttribute("data-bsc-scroll-target", "");
  }

  // On touch screens the address finder scrolls away (iOS Safari), so this zero-height
  // cap docks in its place and keeps the ledger's rounded top edge under the navigator.
  // The stylesheet shows it only while docked.
  const cap = h("div", { class: "bsc-ledger-cap", "aria-hidden": "true" });
  attachDockState(cap, opts.signal);
  const main = h(
    "div",
    { class: "bsc-election__ledger-column" },
    cap,
    opts.showFilters === false && !opts.ballotControl ? null : dock,
    status,
    ledger
  );
  const arrival = selected ? ledger.querySelector<HTMLElement>("[data-bsc-scroll-target]") : null;
  if (arrival) scrollSpy.suspendForScroll(arrival);
  return { nav, main };
}
